import axios from 'axios';
import PDFPreview from './PDFPreview';
import { useAuth } from '../context/AuthContext';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css'; // Import Quill styles
import '../styles/academic.css';
import { useRef, useMemo, useState, useEffect } from 'react';

// --- Custom Video Blot ---
// This tells Quill how to handle <video> tags so it doesn't strip them out.
const BlockEmbed = Quill.import('blots/block/embed');
class VideoBlot extends BlockEmbed {
    static create(value) {
        let node = super.create();
        node.setAttribute('src', value);
        node.setAttribute('controls', true);
        node.setAttribute('style', "max-width: 100%; display: block; margin: 10px auto; border: 1px solid #ccc; border-radius: 4px;");
        return node;
    }

    static value(node) {
        return node.getAttribute('src');
    }
}
VideoBlot.blotName = 'video-file'; // Custom blot name
VideoBlot.tagName = 'video';       // Render as <video> tag
Quill.register(VideoBlot);
// -------------------------

const UnifiedPage = ({ category, title }) => {
    const { currentUser, isAdmin: isRealAdmin, login } = useAuth();
    const [posts, setPosts] = useState([]);

    // Local state to control UI admin mode (can be toggled off even if real admin)
    const [isAdmin, setIsAdmin] = useState(false);

    // Sync local admin state with real admin status on load
    useEffect(() => {
        if (isRealAdmin) {
            setIsAdmin(true);
        }
    }, [isRealAdmin]);

    const [showForm, setShowForm] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        tags: '',
        media: [],
        publishedAt: new Date().toISOString().split('T')[0]
    });
    const [isUploading, setIsUploading] = useState(false); // Loading state
    const [selectedImage, setSelectedImage] = useState(null); // Lightbox state

    // ... (rest of file)

    // Edit Handler
    const handleEdit = (post) => {
        setFormData({
            title: post.title,
            content: post.content,
            tags: post.tags ? post.tags.join(', ') : '',
            media: post.media || [],
            publishedAt: post.publishedAt ? new Date(post.publishedAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
        });
        setEditingId(post._id);
        setIsEditing(true);
        setShowForm(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Editor Ref
    const quillRef = useRef(null);

    // Handler for Attachment Grid (New)
    const handleAttachmentUpload = () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*,video/*,application/pdf');
        input.click();

        input.onchange = async () => {
            const file = input.files[0];
            if (!file) return;

            const uploadData = new FormData();
            uploadData.append('image', file);
            setIsUploading(true);

            try {
                const res = await axios.post('/api/upload-image', uploadData);
                const url = res.data.url;
                let type = 'image';
                if (file.type.startsWith('video/')) type = 'video';
                if (file.type === 'application/pdf') type = 'pdf';

                // Add to media array
                setFormData(prev => ({
                    ...prev,
                    media: [...(prev.media || []), { type, url, filename: file.name }]
                }));

            } catch (error) {
                const msg = error.response ? error.response.data.error : error.message;
                alert(`Upload failed: ${msg}`);
            } finally {
                setIsUploading(false);
            }
        };
    };

    // Custom Media Handler (Legacy Inline)
    const mediaHandler = () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*,video/*'); // Accept both
        input.click();

        input.onchange = async () => {
            const file = input.files[0];
            if (file) {
                const formData = new FormData();
                formData.append('image', file);
                setIsUploading(true);

                try {
                    const res = await axios.post('/api/upload-image', formData);
                    const range = quillRef.current.getEditor().getSelection();
                    const url = res.data.url;

                    if (file.type.startsWith('video/')) {
                        // Use our custom 'video-file' blot
                        quillRef.current.getEditor().insertEmbed(range.index, 'video-file', url);
                    } else {
                        quillRef.current.getEditor().insertEmbed(range.index, 'image', url);
                    }

                    // Force state update to ensure content is saved
                    setTimeout(() => {
                        setFormData(prev => ({ ...prev, content: quillRef.current.getEditor().root.innerHTML }));
                    }, 0);

                } catch (error) {
                    const msg = error.response ? error.response.data.error : error.message;
                    console.error('Error uploading media:', error);
                    alert(`Failed to upload media: ${msg}`);
                } finally {
                    setIsUploading(false);
                }
            }
        };
    };

    // Quill Modules
    const modules = useMemo(() => ({
        toolbar: {
            container: [
                [{ 'header': [1, 2, false] }],
                ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                ['link', 'image', 'video'], // Added video button
                ['clean']
            ],
            handlers: {
                image: mediaHandler,
                video: mediaHandler // Use same handler for video button
            }
        }
    }), []);

    // Comment State (map postId -> comment text)
    const [commentData, setCommentData] = useState({});
    const [expandedComments, setExpandedComments] = useState({}); // Toggle view comments

    // Refetch when category changes
    useEffect(() => {
        fetchPosts();
    }, [category]);

    const fetchPosts = async () => {
        try {
            // Pass category to filter posts
            const res = await axios.get(`/api/blog-posts?published=true&category=${category}`);
            setPosts(res.data.posts || []);
        } catch (error) {
            console.error('Error fetching posts:', error);
        }
    };

    const handleCommentSubmit = async (postId) => {
        const content = commentData[postId];
        if (!content) return;

        try {
            await axios.post(`/api/blog-posts/${postId}/comments`, { content });
            // Refresh posts to see new comment (or we could optimistically update)
            fetchPosts();
            setCommentData({ ...commentData, [postId]: '' });
            alert('Comment posted!');
        } catch (error) {
            console.error('Error posting comment:', error);
            alert('Failed to post comment. Are you logged in?');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const tagsArray = formData.tags ? formData.tags.split(',').map(tag => tag.trim()) : [];
            const payload = { ...formData, tags: tagsArray, published: true, category };

            if (isEditing && editingId) {
                // UPDATE
                await axios.put(`/api/blog-posts/${editingId}`, payload);
            } else {
                // CREATE
                await axios.post('/api/blog-posts', payload);
            }

            setShowForm(false);
            setIsEditing(false);
            setEditingId(null);
            fetchPosts();
            setFormData({ title: '', content: '', tags: '', media: [], publishedAt: new Date().toISOString().split('T')[0] });
        } catch (error) {
            console.error(error);
            const errorMessage = error.response?.data?.error || error.message;
            alert(`Failed to save post: ${errorMessage}`);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this entry?')) return;
        try {
            await axios.delete(`/api/blog-posts/${id}`);
            fetchPosts();
        } catch (error) {
            console.error(error);
        }
    };

    // Helper for YouTube Embed
    const getYoutubeId = (url) => {
        if (!url) return null;
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
        const match = url.match(regExp);
        return (match && match[2].length === 11) ? match[2] : null;
    };

    const handleAddYoutubeLink = () => {
        const link = prompt("Enter YouTube URL:");
        if (link && getYoutubeId(link)) {
            setFormData(prev => ({
                ...prev,
                media: [...(prev.media || []), { type: 'youtube', url: link }]
            }));
        } else if (link) {
            alert("Invalid YouTube URL");
        }
    };

    return (
        <div>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px' }}>
                <h1 className="section-title">{title}</h1>

                {/* Admin Mode Toggle for valid admins */}
                {isRealAdmin && (
                    <button
                        onClick={() => setIsAdmin(!isAdmin)}
                        style={{ background: 'transparent', border: '1px solid black', padding: '5px 10px', fontSize: '0.7em', cursor: 'pointer', fontFamily: 'monospace' }}
                    >
                        [{isAdmin ? 'EXIT_ADMIN' : 'ADMIN'}]
                    </button>
                )}
            </div>


            {/* Admin Console */}
            {
                isAdmin && (
                    <div style={{ marginBottom: '30px', padding: '20px', border: '1px dashed red' }}>
                        <h3 style={{ marginTop: 0 }}>ADMIN_CONSOLE ({category})</h3>
                        {!showForm ? (
                            <button onClick={() => {
                                setShowForm(true);
                                setFormData({ title: '', content: '', tags: '', media: [], publishedAt: new Date().toISOString().split('T')[0] });
                                setIsEditing(false);
                            }} className="tag" style={{ cursor: 'pointer' }}>+ NEW ENTRY</button>
                        ) : (
                            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <input placeholder="TITLE" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} style={{ padding: '10px', fontFamily: 'inherit', flex: 1 }} required />
                                    <input type="date" value={formData.publishedAt} onChange={e => setFormData({ ...formData, publishedAt: e.target.value })} style={{ padding: '10px', fontFamily: 'inherit' }} />
                                </div>

                                {/* Rich Text Editor */}
                                {isUploading && <div style={{ color: 'blue', fontStyle: 'italic', marginBottom: '5px' }}>Uploading image... Please wait...</div>}
                                <div style={{ backgroundColor: 'white', marginBottom: '20px' }}>
                                    <ReactQuill
                                        ref={quillRef}
                                        theme="snow"
                                        value={formData.content}
                                        onChange={(value) => setFormData(prev => ({ ...prev, content: value }))}
                                        modules={modules}
                                        style={{ height: '300px' }}
                                    />
                                </div>

                                {/* Media Attachments Section */}
                                <div style={{ border: '1px dashed #ccc', padding: '15px', marginBottom: '10px' }}>
                                    <strong>ATTACHMENTS (Grid Layout)</strong>
                                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '10px' }}>
                                        {formData.media && formData.media.map((item, index) => (
                                            <div key={index} style={{ width: '100px', height: '100px', position: 'relative', border: '1px solid #ddd' }}>
                                                {item.type === 'youtube' ? (
                                                    <img
                                                        src={`https://img.youtube.com/vi/${getYoutubeId(item.url)}/0.jpg`}
                                                        alt="YouTube Thumbnail"
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    />
                                                ) : item.type === 'video' ? (
                                                    <video src={item.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                ) : (
                                                    <img src={item.url} alt="attachment" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const newMedia = formData.media.filter((_, i) => i !== index);
                                                        setFormData({ ...formData, media: newMedia });
                                                    }}
                                                    style={{ position: 'absolute', top: 0, right: 0, background: 'red', color: 'white', border: 'none', cursor: 'pointer' }}
                                                >x</button>
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            onClick={handleAttachmentUpload}
                                            style={{ width: '100px', height: '100px', cursor: 'pointer', background: '#f0f0f0', border: '1px solid #ccc' }}
                                        >
                                            + ADD MEDIA
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleAddYoutubeLink}
                                            style={{ width: '100px', height: '100px', cursor: 'pointer', background: '#f0f0f0', border: '1px solid #ccc' }}
                                        >
                                            + ADD YOUTUBE LINK
                                        </button>
                                    </div>
                                </div>

                                <input placeholder="TAGS (comma separated)" value={formData.tags} onChange={e => setFormData({ ...formData, tags: e.target.value })} style={{ padding: '10px', fontFamily: 'inherit', marginTop: '20px', position: 'relative', zIndex: 10 }} />
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <button type="submit" className="tag" style={{ background: 'black', color: 'white', cursor: 'pointer' }}>{isEditing ? 'UPDATE' : 'PUBLISH'}</button>
                                    <button type="button" onClick={() => { setShowForm(false); setIsEditing(false); }} className="tag" style={{ cursor: 'pointer' }}>CANCEL</button>
                                </div>
                            </form>
                        )}
                    </div>
                )
            }

            {/* Blog Posts */}
            <div className="bento-grid">
                {posts.map(post => {
                    const comments = post.comments || [];
                    const isExpanded = expandedComments[post._id];

                    return (
                        <div key={post._id} className={`bento-card ${category === 'networking' ? 'col-span-2' : 'col-span-4'}`} style={{ marginBottom: '20px' }}>
                            {/* Post Content */}
                            <div className="card-header">
                                <span className="card-title">{post.title}</span>
                                <span style={{ fontSize: '0.8rem' }}>{new Date(post.publishedAt || post.createdAt).toLocaleDateString()}</span>
                            </div>

                            {/* Text Content */}
                            <div
                                className="card-content"
                                style={{
                                    marginBottom: '20px',
                                    maxHeight: '300px', // Reduced since we have grid below
                                    overflowY: 'auto',
                                    resize: 'vertical',
                                    padding: '15px',
                                    backgroundColor: 'white'
                                }}>
                                <div dangerouslySetInnerHTML={{ __html: post.content }} />
                            </div>

                            {/* MEDIA GRID (New) */}
                            {post.media && post.media.length > 0 && (
                                <div className="media-grid" style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                                    gap: '15px',
                                    marginTop: '20px',
                                    padding: '15px',
                                    backgroundColor: '#f9f9f9',
                                    borderTop: '1px solid #eee'
                                }}>
                                    {post.media.map((item, idx) => (
                                        <div key={idx} style={{ position: 'relative', cursor: 'pointer', border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden', backgroundColor: 'black' }}>
                                            {item.type === 'youtube' ? (
                                                <iframe
                                                    src={`https://www.youtube.com/embed/${getYoutubeId(item.url)}`}
                                                    style={{ width: '100%', height: '200px', border: 0 }}
                                                    allowFullScreen
                                                    title="YouTube Video"
                                                />
                                            ) : item.type === 'video' ? (
                                                <video
                                                    src={item.url}
                                                    controls
                                                    controlsList="nodownload"
                                                    onContextMenu={e => e.preventDefault()}
                                                    style={{ width: '100%', height: '200px', objectFit: 'contain', display: 'block' }}
                                                />
                                            ) : item.type === 'pdf' ? (
                                                <div style={{ position: 'relative', width: '100%', height: '200px', background: '#f9f9f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setSelectedImage(item.url)}>
                                                    <PDFPreview file={item.url} width={300} />
                                                    <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', background: 'rgba(0,0,0,0.05)', textAlign: 'center', padding: '5px', fontSize: '0.8rem', color: '#555', fontWeight: 'bold' }}>
                                                        [OPEN PDF]
                                                    </div>
                                                </div>
                                            ) : (
                                                <img
                                                    src={item.url}
                                                    alt="grid item"
                                                    onClick={() => setSelectedImage(item.url)}
                                                    style={{ width: '100%', height: '200px', objectFit: 'cover', display: 'block' }}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Tags & Delete */}
                            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', marginTop: '15px' }}>
                                {post.tags && post.tags.map((tag, i) => <span key={i} className="tag">#{tag}</span>)}
                                {isAdmin && (
                                    <>
                                        <button onClick={() => handleEdit(post)} style={{ color: 'blue', cursor: 'pointer', border: 'none', background: 'none', marginRight: '10px' }}>[EDIT]</button>
                                        <button onClick={() => handleDelete(post._id)} style={{ color: 'red', cursor: 'pointer', border: 'none', background: 'none' }}>[DELETE]</button>
                                    </>
                                )}
                            </div>

                            <hr style={{ border: '0', borderTop: '1px solid #eee', marginBottom: '15px' }} />

                            {/* Interaction Section */}
                            <div style={{ backgroundColor: '#f9f9f9', padding: '15px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                    <strong>COMMENTS ({comments.length})</strong>
                                    <button
                                        onClick={() => setExpandedComments({ ...expandedComments, [post._id]: !isExpanded })}
                                        style={{ border: 'none', background: 'none', cursor: 'pointer', textDecoration: 'underline', fontStyle: 'italic' }}
                                    >
                                        {isExpanded ? 'Hide' : 'Show'}
                                    </button>
                                </div>

                                {isExpanded && (
                                    <>
                                        {comments.map(comment => (
                                            <div key={comment._id} style={{ marginBottom: '10px', borderLeft: '2px solid #ddd', paddingLeft: '10px' }}>
                                                <div style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>{comment.author}</div>
                                                <div style={{ fontSize: '0.9rem' }}>{comment.content}</div>
                                            </div>
                                        ))}
                                        {comments.length === 0 && <p style={{ fontStyle: 'italic', fontSize: '0.8rem', color: '#666' }}>No comments yet.</p>}
                                    </>
                                )}

                                {/* Comment Form */}
                                {currentUser ? (
                                    <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
                                        <input
                                            placeholder="Write a comment..."
                                            value={commentData[post._id] || ''}
                                            onChange={e => setCommentData({ ...commentData, [post._id]: e.target.value })}
                                            style={{ flex: 1, padding: '5px', fontFamily: 'inherit' }}
                                        />
                                        <button onClick={() => handleCommentSubmit(post._id)} className="tag" style={{ cursor: 'pointer' }}>POST</button>
                                    </div>
                                ) : (
                                    <div style={{ marginTop: '10px', fontSize: '0.9rem' }}>
                                        <span onClick={login} style={{ color: '#db4437', cursor: 'pointer', textDecoration: 'underline' }}>Login with Google</span> to comment.
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {posts.length === 0 && <p>No entries found for {category}.</p>}

            {/* Lightbox Modal */}
            {selectedImage && (
                <div
                    className="lightbox-overlay"
                    onClick={() => setSelectedImage(null)}
                >
                    <div className="lightbox-content" style={selectedImage.toLowerCase().endsWith('.pdf') ? { width: '90%', height: '90%', maxWidth: '1000px' } : {}}>
                        {selectedImage.toLowerCase().endsWith('.pdf') ? (
                            <embed
                                src={selectedImage}
                                type="application/pdf"
                                style={{ width: '100%', height: '100%', border: 'none', background: 'white' }}
                            />
                        ) : (
                            <img src={selectedImage} alt="Full view" />
                        )}
                        <button className="lightbox-close" onClick={() => setSelectedImage(null)}>×</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UnifiedPage;
